import { Checkbox, Label, TextInput, Textarea } from "flowbite-react"
import { ErrorMessage, useFormikContext } from "formik"
import moment from "moment"
import React, { useState } from "react"
import { CountryData } from "react-phone-input-2"

import InputRadio from "@components/elements/CustomRadio"
import styles from "@components/elements/CustomRadio/inputbox.module.css"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import MonoDatePicker from "@components/elements/DatePicker"
import FormGroup from "@components/elements/FormGroup"
import MonoMonthYearDay from "@components/elements/MonthYearDayDatePicker"
import PhoneInput from "@components/elements/PhoneInput"
import RichTextEditor from "@components/elements/RichText"
import MonoSelect from "@components/elements/Select"
import UploadFile from "@components/elements/UploadFile"
import CalendarIcon from "@icons/Calendar"
import { ProductFieldType } from "@mono/essentials/types/OrderTypes"
import { toLocalDate } from "@mono/essentials/utils/Common"

import participantStyles from "./ParticipationDetailsForm.module.css"

interface ParticipantsFieldsProps {
  fieldsData: ProductFieldType[]
  fieldGroupFormName: string
  fieldArrayIndex: number
  onPhoneInputChange?: (value: string, country: Record<string, never> | CountryData) => void
  type?: "ask_per_participant" | "ask_per_order" | "ask_per_booking" | "ask_per_item"
  isWidgetPage?: boolean
}

const ParticipantsFields = ({
  fieldsData,
  fieldGroupFormName,
  fieldArrayIndex,
  type = "ask_per_participant",
  onPhoneInputChange,
  isWidgetPage,
}: ParticipantsFieldsProps) => {
  const { getFieldProps, errors, touched, setFieldValue, values } = useFormikContext()

  const getErrorStatus = (name: string) =>
    (errors as unknown as any)[fieldGroupFormName] &&
    (errors as unknown as any)[fieldGroupFormName][fieldArrayIndex] &&
    (errors as unknown as any)[fieldGroupFormName][fieldArrayIndex][name] &&
    (touched as unknown as any)[fieldGroupFormName] &&
    (touched as unknown as any)[fieldGroupFormName][fieldArrayIndex] &&
    (touched as unknown as any)[fieldGroupFormName][fieldArrayIndex][name]

  return (
    <>
      {fieldsData
        ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        ?.map((fieldData, index) => {
          if (
            (isWidgetPage
              ? fieldData.allowed_online_booking
              : fieldData.allowed_internal_booking) &&
            fieldData?.ask?.includes(type)
          ) {
            switch (fieldData.field.type) {
              case "inline":
                return (
                  <div
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                    className={"col-span-2"}
                  >
                    <DangerouslyHTMLTextPriview
                      content={fieldData.label}
                      className={"text12Regular"}
                    />
                  </div>
                )
              case "delivery_date":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label ?? "Date"}
                    // All the fields with rules are rendered to the full column width
                    className={`w-full col-span-2`}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <MonoDatePicker
                      popperProps={{ strategy: "fixed" }}
                      value={
                        values?.[fieldGroupFormName]?.[fieldArrayIndex]?.[fieldData.field.name]
                          ? moment(
                              values?.[fieldGroupFormName]?.[fieldArrayIndex]?.[
                                fieldData.field.name
                              ],
                              "YYYY-MM-DD"
                            ).format("DD-MM-YY")
                          : ""
                      }
                      onChange={(selectedDate) => {
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                          moment(selectedDate).format("YYYY-MM-DD")
                        )
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                          fieldData.id
                        )
                      }}
                      inputFieldProps={{
                        ...getFieldProps(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                        ),
                        placeholder: fieldData.placeholder ?? "Please select date",
                        color: getErrorStatus(fieldData.field.name) ? "failure" : undefined,
                        icon: () => <CalendarIcon width={16} height={16} />,
                        className: "bg-whiteBlack-100",
                      }}
                      // excludeDates={getproductUnavailableDates()}
                      minDate={toLocalDate()}
                      popperClassName={`!p-0 ${participantStyles.productDatePicker}`}
                      showSpinner
                    >
                      <div className={"flex flex-row items-center justify-start gap-2"}>
                        <span className={"h-1 w-1 rounded-full bg-primary-600"} />
                        <span className={"text12Semibold text-primary-600"}>Available</span>
                        <span className={"h-1 w-1 rounded-full bg-neutral-500"} />
                        <span className={"text12Semibold text-neutral-500"}>Unavailable</span>
                      </div>
                    </MonoDatePicker>
                  </FormGroup>
                )
              case "date":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label ?? "Date"}
                    className={`w-full text-neutral-800 col-span-2`}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <MonoMonthYearDay
                      selectedValue={getFieldProps(
                        `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                      )}
                      onValueChange={(date: any) => {
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                          date
                        )
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                          fieldData.id
                        )
                      }}
                    />
                  </FormGroup>
                )
              case "list":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={`w-full col-span-2`}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <MonoSelect
                      placeholder={fieldData.placeholder}
                      {...getFieldProps(
                        `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                      )}
                      options={fieldData.options?.map((option) => ({
                        label: option.name,
                        value: option.label,
                      }))}
                      onValueChange={() => {
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                          fieldData.id
                        )
                      }}
                      inputId={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                      hasError={getErrorStatus(fieldData.field.name)}
                      className={" bg-whiteBlack-100"}
                      menuPosition={"fixed"}
                      isSearchable={false}
                    />
                  </FormGroup>
                )
              case "rich_text":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    label={fieldData.label}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    className={"w-full col-span-2 py-3 md:py-0"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <RichTextEditor
                      name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                      value={
                        getFieldProps(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                        ).value
                      }
                      wrapperClassName={"w-full block"}
                      onChange={(val) => {
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                          val
                        )
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                          fieldData.id
                        )
                      }}
                    />
                  </FormGroup>
                )
              case "agreement":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    label={fieldData.label}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    className={"w-full col-span-2"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <div className={"col-span-2 flex w-full items-center gap-2"} key={index}>
                      {fieldData?.options?.map((item, index) => (
                        <div className={"col-span-2 flex w-fit gap-3 items-start"} key={index}>
                          <Checkbox
                            {...getFieldProps(
                              `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                            )}
                            checked={
                              getFieldProps(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                              )?.value === "yes"
                            }
                            name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                            id={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_${index}`}
                            onChange={(e) => {
                              setFieldValue(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                                fieldData.id
                              )
                              if (e.target.checked) {
                                setFieldValue(
                                  `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                                  item.label
                                )
                              } else {
                                setFieldValue(
                                  `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                                  ""
                                )
                              }
                            }}
                            className={"cursor-pointer mt-1 text-primary-600"}
                          />
                          <Label
                            htmlFor={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_${index}`}
                          >
                            <DangerouslyHTMLTextPriview
                              content={item.name}
                              className={"paragraph14Regular"}
                            />
                          </Label>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                )
              case "radio":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={"col-span-2 w-full"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <div
                      className={`${styles.RadioGroup} w-full grid xl:grid-cols-4 justify-center xl:gap-2 gap-1 grid-cols-3`}
                    >
                      {fieldData?.options?.map((item, index) => (
                        <div key={index}>
                          <InputRadio
                            id={index}
                            name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                            key={`radio_field_${index}`}
                            radioKey={`radio_field_${index}`}
                            label={item.name}
                            value={item.label}
                            {...getFieldProps(
                              `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                            )}
                            isSelected={
                              getFieldProps(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                              ).value === item.label
                                ? index
                                : undefined
                            }
                            className={"!m-0"}
                            handleClick={() => {
                              setFieldValue(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                                item.label
                              )
                              setFieldValue(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                                fieldData.id
                              )
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                )
              case "checkbox":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={"col-span-2 w-full py-3 md:py-0"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <div className={"flex flex-col items-start gap-2 w-full ml-2 mt-1"}>
                      {fieldData?.options?.map((item, index) => (
                        <div className={"col-span-2 flex w-fit items-center gap-2"} key={index}>
                          <Checkbox
                            checked={(
                              getFieldProps(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                              )?.value || []
                            )?.includes(item?.label)}
                            {...getFieldProps(
                              `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                            )}
                            name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                            id={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_${index}`}
                            onChange={() => {
                              setFieldValue(
                                `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                                fieldData.id
                              )
                              if (
                                (
                                  getFieldProps(
                                    `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                                  )?.value || []
                                )?.includes(item.label)
                              ) {
                                setFieldValue(
                                  `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                                  getFieldProps(
                                    `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                                  ).value.filter((value) => value !== item.label)
                                )
                              } else {
                                setFieldValue(
                                  `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                                  [
                                    ...(getFieldProps(
                                      `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                                    )?.value || []),
                                    item.label,
                                  ]
                                )
                              }
                            }}
                            className={"cursor-pointer"}
                          />
                          <Label
                            htmlFor={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_${index}`}
                            className={"!text12Medium text-neutral-700 cursor-pointer"}
                          >
                            {item.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </FormGroup>
                )
              case "multi":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={"col-span-2 w-full"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <Textarea
                      className={"bg-whiteBlack-100"}
                      {...getFieldProps(
                        `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                      )}
                      placeholder={fieldData.placeholder}
                      rows={4}
                      color={getErrorStatus(fieldData.field.name) ? "failure" : undefined}
                      onChange={(e) => {
                        getFieldProps(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                        ).onChange(e)
                        setFieldValue(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                          fieldData.id
                        )
                      }}
                    />
                  </FormGroup>
                )
              case "file":
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={"col-span-2 w-full bg-whiteBlack-100"}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    <UploadFile
                      description={
                        <p
                          className={
                            "text12Regular mt-2 bg-whiteBlack-100 text-center text-neutral-600"
                          }
                        >
                          Click to upload or drag and <br />
                          drop PNG, JPG, PDF
                        </p>
                      }
                      onchangeUploading={console.log}
                    />
                    <ErrorMessage
                      component={"a"}
                      name={"eMailAttachment"}
                      className={"text-sm-red-error"}
                    />
                  </FormGroup>
                )
              default:
                return (
                  <FormGroup
                    requiredField={Boolean(
                      isWidgetPage
                        ? fieldData.required_for_online_booking
                        : fieldData.required_for_internal_booking
                    )}
                    name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                    label={fieldData.label}
                    className={`w-full py-1 col-span-2`}
                    key={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}-${index}`}
                  >
                    {fieldData.field.name === "phone" ? (
                      <PhoneInput
                        dialCodeName={`${fieldGroupFormName}.${fieldArrayIndex}.phone_code`}
                        name={`${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`}
                        onChange={(value, country) => {
                          setFieldValue(
                            `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`,
                            value
                          )
                          setFieldValue(
                            `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                            fieldData.id
                          )
                          onPhoneInputChange?.(value, country)
                        }}
                      />
                    ) : (
                      <TextInput
                        {...getFieldProps(
                          `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                        )}
                        placeholder={fieldData.placeholder}
                        color={getErrorStatus(fieldData.field.name) ? "failure" : undefined}
                        className={"bg-whiteBlack-100"}
                        autoComplete={"new-password"}
                        onChange={(e) => {
                          getFieldProps(
                            `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}`
                          ).onChange(e)
                          setFieldValue(
                            `${fieldGroupFormName}.${fieldArrayIndex}.${fieldData.field.name}_id`,
                            fieldData.id
                          )
                        }}
                      />
                    )}
                  </FormGroup>
                )
            }
          } else return null
        })}
    </>
  )
}

export default ParticipantsFields
